import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { BASE_URL } from "../utilities/BASE_URL";
import { fetchWithToken, postWithToken } from "../utilities/fetchWithToken";

export const fetchRooms = createAsyncThunk(
        "rooms/fetchRooms",
        async(_, {rejectWithValue}) => {
            try {
                const response = await fetchWithToken(`${BASE_URL}/roomslist`);

                const data = await response.json();
                if(!response.ok) {
                    return rejectWithValue(data.error || "Failed to fetch rooms")
                }

                return data;
            } catch (error) {
                return rejectWithValue(error.message);
            }

        }
)

// The Sit-and-Go lobby tab: tournaments with buy-in/payouts and live registration state
// ("started" rows render as running and aren't joinable).
export const fetchSngRooms = createAsyncThunk(
        "rooms/fetchSngRooms",
        async(_, {rejectWithValue}) => {
            try {
                const response = await fetchWithToken(`${BASE_URL}/roomslist/sitandgo`);

                const data = await response.json();
                if(!response.ok) {
                    return rejectWithValue(data.error || "Failed to fetch Sit-and-Go rooms")
                }

                return data;
            } catch (error) {
                return rejectWithValue(error.message);
            }

        }
)

// The Tournaments lobby tab: admin-scheduled multi-table events. Rows carry scheduledStartAt,
// buy-in, status, whether THIS user is registered, and live counts.
export const fetchTournaments = createAsyncThunk(
        "rooms/fetchTournaments",
        async(_, {rejectWithValue}) => {
            try {
                const response = await fetchWithToken(`${BASE_URL}/roomslist/tournaments`);
                const data = await response.json();
                if(!response.ok) {
                    return rejectWithValue(data.error || "Failed to fetch tournaments")
                }
                return data;
            } catch (error) {
                return rejectWithValue(error.message);
            }
        }
)

// Register for a tournament (buy-in debited now). Re-fetches the list so the row flips to
// "Registered". A late registration also seats the player — the server pushes TOURNAMENT_STARTED.
export const registerTournament = createAsyncThunk(
        "rooms/registerTournament",
        async(id, {rejectWithValue, dispatch}) => {
            try {
                const response = await postWithToken(`${BASE_URL}/tournaments/${id}/register`, {});
                const data = await response.json().catch(() => ({}));
                if(!response.ok) {
                    return rejectWithValue(data.error || "Failed to register")
                }
                dispatch(fetchTournaments());
                return data;
            } catch (error) {
                return rejectWithValue(error.message);
            }
        }
)

export const unregisterTournament = createAsyncThunk(
        "rooms/unregisterTournament",
        async(id, {rejectWithValue, dispatch}) => {
            try {
                const response = await postWithToken(`${BASE_URL}/tournaments/${id}/unregister`, {});
                const data = await response.json().catch(() => ({}));
                if(!response.ok) {
                    return rejectWithValue(data.error || "Failed to unregister")
                }
                dispatch(fetchTournaments());
                return data;
            } catch (error) {
                return rejectWithValue(error.message);
            }
        }
)

const roomSlice = createSlice({
    name: "rooms",
    initialState: {
      list: [],
      sngList: [],
      tournamentList: [],
      loading: false,
      error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
      builder
        .addCase(fetchRooms.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(fetchRooms.fulfilled, (state, action) => {
          state.loading = false;
          state.list = action.payload;
        })
        .addCase(fetchRooms.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        })
        .addCase(fetchSngRooms.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(fetchSngRooms.fulfilled, (state, action) => {
          state.loading = false;
          state.sngList = action.payload;
        })
        .addCase(fetchSngRooms.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        })
        .addCase(fetchTournaments.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(fetchTournaments.fulfilled, (state, action) => {
          state.loading = false;
          state.tournamentList = action.payload;
        })
        .addCase(fetchTournaments.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        })
        .addCase(registerTournament.rejected, (state, action) => {
          state.error = action.payload;
        })
        .addCase(unregisterTournament.rejected, (state, action) => {
          state.error = action.payload;
        });
    },
  });
  
  export default roomSlice.reducer;
